import React, { forwardRef } from "react";
import ReactDatePicker from "react-datepicker";
import { icons } from "../../../assets/icons";
import "react-datepicker/dist/react-datepicker.css";
import "./datep.css";
import moment from "moment";

/** Must be module-level: defining forwardRef inside DatePicker remounts every render and breaks the calendar. */
const DatePickerTrigger = forwardRef(
  (
    {
      onClick,
      displayText,
      datePickerClassName = "",
      datePickerTextClassName = "",
      value,
    },
    ref
  ) => {
    const text = displayText || value || "";
    return (
      <div
        className={`flex justify-between items-center min-w-[28rem] h-[5rem] border-[1px] border-[var(--borderGray1)] pl-3 pr-5 rounded-[0.4rem] cursor-pointer ${datePickerClassName}`}
        onClick={onClick}
        ref={ref}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.(e);
          }
        }}
      >
        <div className={`font-size-small ${datePickerTextClassName}`}>
          {text}
        </div>
        <img
          src={icons.calendarIcon}
          alt=""
          className="h-[2.2rem] w-[2.2rem] ml-2 pointer-events-none"
        />
      </div>
    );
  }
);
DatePickerTrigger.displayName = "DatePickerTrigger";

const DatePicker = ({
  title = "",
  wrapperClassName = "",
  datePickerClassName = "",
  datePickerTextClassName = "",
  required = false,
  labelClassName = "",
  dateRange = false,
  startDate = new Date(),
  endDate,
  onChange,
  error,
  minDate,
  maxDate,
  disabled = false,
  isSecondMinuteTimeStampActive = false,
  withPortal = false,
}) => {
  const dateFormat = "DD MMM yyy";
  const dateToShow =
    startDate && !endDate
      ? moment(startDate).format(
          isSecondMinuteTimeStampActive ? dateFormat : dateFormat
        )
      : startDate && endDate
      ? `${moment(startDate).format(
          isSecondMinuteTimeStampActive ? dateFormat : dateFormat
        )} - ${moment(endDate).format(
          isSecondMinuteTimeStampActive ? dateFormat : dateFormat
        )}`
      : "";

  return (
    <div
      className={`${wrapperClassName} w-100`}
      style={{ fontSize: "100% !important" }}
      id="date-pick-change"
    >
      {title ? (
        <div
          className={`font-size-small text-[var(--blackColor)] mb-2 ${labelClassName}`}
        >
          {title}
          {required ? <span className="text-[#e55353]"> *</span> : null}
        </div>
      ) : null}

      <ReactDatePicker
        calendarClassName="w-full"
        selected={startDate}
        onChange={onChange}
        startDate={startDate}
        endDate={endDate}
        selectsRange={dateRange}
        customInput={
          <DatePickerTrigger
            displayText={dateToShow}
            datePickerClassName={datePickerClassName}
            datePickerTextClassName={datePickerTextClassName}
          />
        }
        minDate={minDate}
        maxDate={maxDate}
        monthsShown={dateRange ? 2 : 1}
        disabled={disabled}
        withPortal={withPortal}
        popperClassName="kv-datepicker-popper"
        renderCustomHeader={({
          monthDate,
          customHeaderCount,
          decreaseMonth,
          increaseMonth,
        }) => (
          <div>
            <button
              aria-label="Previous Month"
              className={
                "react-datepicker__navigation react-datepicker__navigation--previous"
              }
              style={customHeaderCount === 1 ? { visibility: "hidden" } : null}
              onClick={decreaseMonth}
              type="button"
            >
              <span
                className={
                  "react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"
                }
              >
                {"<"}
              </span>
            </button>
            <span className="react-datepicker__current-month">
              {monthDate.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              aria-label="Next Month"
              className={
                "react-datepicker__navigation react-datepicker__navigation--next"
              }
              style={customHeaderCount === 0 ? { visibility: "hidden" } : null}
              onClick={increaseMonth}
              type="button"
            >
              <span
                className={
                  "react-datepicker__navigation-icon react-datepicker__navigation-icon--next"
                }
              >
                {">"}
              </span>
            </button>
          </div>
        )}
      />
      {error ? (
        <div className={`font-size-small text-[var(--textRed)] mt-2`}>
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default DatePicker;
